/**
 * `AddonsTab` — add-on catalog list rendered inside the Plans view
 * (ADR-0263 S1 §2.5). Lives under a tab strip in the Plans view
 * rather than a new top-level nav entry (the sidebar is already
 * crowded — founder rule 2026-10-10).
 *
 * One round-trip (`GET /internal/admin/addons`) returns every add-on
 * bundled with its prices. Row click opens the editor.
 */

import { useEffect, useState } from 'react';

import type { AdminPlanAddonDetail } from '@sill/types/api';

import { useAdminApi } from '../../api/api-context.js';
import { ApiError } from '../../api/http-client.js';
import type { SetView } from '../../app/view-router.js';
import { Card } from '../../components/atoms/Card.js';
import { Pill } from '../../components/atoms/Pill.js';
import { useC } from '../../lib/theme.js';
import { formatIsoCompact } from '../../lib/format.js';
import { formatPriceLabel, findPrice, planStatusTone } from './plan-helpers.js';
import { addonKindLabel, addonUnitHint } from './addon-helpers.js';

const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";

export interface AddonsTabProps {
  setView: SetView;
}

export function AddonsTab({ setView }: AddonsTabProps): JSX.Element {
  const C = useC();
  const api = useAdminApi();

  const [addons, setAddons] = useState<readonly AdminPlanAddonDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async (): Promise<void> => {
      try {
        const res = await api.getAddons();
        if (!cancelled) setAddons(res.addons);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.kind === 'unauthorized') return;
        const reason =
          err instanceof ApiError && err.server_reason !== undefined
            ? ` (${err.server_reason})`
            : '';
        setError(`Could not load add-ons${reason}.`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api]);

  if (error !== null) {
    return (
      <Card style={{ padding: 16 }}>
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.coralFg,
            letterSpacing: 2,
            textTransform: 'uppercase',
            marginBottom: 6,
          }}
        >
          Load failed
        </div>
        <div style={{ fontFamily: sansFont, fontSize: 13, color: C.text }}>
          {error}
        </div>
      </Card>
    );
  }

  if (loading && addons.length === 0) {
    return (
      <Card style={{ padding: 24 }}>
        <div
          className="text-center"
          style={{
            fontFamily: monoFont,
            fontSize: 11,
            color: C.muted,
            letterSpacing: 1.5,
          }}
        >
          Loading…
        </div>
      </Card>
    );
  }

  if (addons.length === 0) {
    return (
      <Card style={{ padding: 24 }}>
        <div
          className="text-center"
          style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}
        >
          No add-ons yet. Click <strong>New add-on</strong> to draft one.
        </div>
      </Card>
    );
  }

  return (
    <>
      {/* Desktop / tablet — table */}
      <Card className="hidden sm:block" style={{ overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table
            style={{
              borderCollapse: 'collapse',
              fontFamily: sansFont,
              width: '100%',
              minWidth: 880,
            }}
          >
            <thead>
              <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                <Th>Name</Th>
                <Th>Status</Th>
                <Th>Kind</Th>
                <Th>Unit</Th>
                <Th>Monthly</Th>
                <Th>Yearly</Th>
                <Th>Updated</Th>
              </tr>
            </thead>
            <tbody>
              {addons.map((row, idx) => (
                <AddonRow
                  key={row.addon.addon_id}
                  detail={row}
                  isLast={idx === addons.length - 1}
                  onClick={(): void =>
                    setView('addon-detail', { addonId: row.addon.addon_id })
                  }
                />
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Mobile — stacked cards */}
      <div className="sm:hidden grid grid-cols-1 gap-2">
        {addons.map((row) => (
          <AddonMobileCard
            key={row.addon.addon_id}
            detail={row}
            onClick={(): void =>
              setView('addon-detail', { addonId: row.addon.addon_id })
            }
          />
        ))}
      </div>
    </>
  );
}

function Th({ children }: { children: React.ReactNode }): JSX.Element {
  const C = useC();
  return (
    <th
      className="text-left px-4 py-3"
      style={{
        fontFamily: monoFont,
        fontSize: 9,
        color: C.muted,
        letterSpacing: 2,
        textTransform: 'uppercase',
        fontWeight: 500,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </th>
  );
}

function AddonRow({
  detail,
  isLast,
  onClick,
}: {
  detail: AdminPlanAddonDetail;
  isLast: boolean;
  onClick: () => void;
}): JSX.Element {
  const C = useC();
  const monthly = findPrice(detail.prices, 'monthly');
  const yearly = findPrice(detail.prices, 'yearly');
  return (
    <tr
      onClick={onClick}
      className="cursor-pointer"
      style={{
        borderBottom: isLast ? 'none' : `1px solid ${C.borderSoft}`,
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = C.surface2)}
      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
    >
      <td className="px-4 py-3">
        <div
          style={{
            fontFamily: sansFont,
            fontSize: 13,
            color: C.text,
            fontWeight: 500,
          }}
        >
          {detail.addon.name}
        </div>
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.dim,
            marginTop: 2,
          }}
        >
          {detail.addon.slug}
        </div>
      </td>
      <td className="px-4 py-3">
        <Pill tone={planStatusTone(detail.addon.status)}>
          {detail.addon.status}
        </Pill>
      </td>
      <td
        className="px-4 py-3"
        style={{
          fontFamily: monoFont,
          fontSize: 11,
          color: C.body,
          whiteSpace: 'nowrap',
        }}
      >
        {addonKindLabel(detail.addon.kind)}
      </td>
      <td
        className="px-4 py-3"
        style={{
          fontFamily: monoFont,
          fontSize: 11,
          color: C.body,
          whiteSpace: 'nowrap',
        }}
      >
        {detail.addon.unit_value.toLocaleString('en-US')} {addonUnitHint(detail.addon.kind)}
      </td>
      <td
        className="px-4 py-3"
        style={{
          fontFamily: monoFont,
          fontSize: 11,
          color: monthly === undefined ? C.dim : C.body,
          whiteSpace: 'nowrap',
        }}
      >
        {formatPriceLabel(monthly)}
      </td>
      <td
        className="px-4 py-3"
        style={{
          fontFamily: monoFont,
          fontSize: 11,
          color: yearly === undefined ? C.dim : C.body,
          whiteSpace: 'nowrap',
        }}
      >
        {formatPriceLabel(yearly)}
      </td>
      <td
        className="px-4 py-3"
        style={{
          fontFamily: monoFont,
          fontSize: 11,
          color: C.muted,
          whiteSpace: 'nowrap',
        }}
      >
        {formatIsoCompact(detail.addon.updated_at)}
      </td>
    </tr>
  );
}

function AddonMobileCard({
  detail,
  onClick,
}: {
  detail: AdminPlanAddonDetail;
  onClick: () => void;
}): JSX.Element {
  const C = useC();
  const monthly = findPrice(detail.prices, 'monthly');
  const yearly = findPrice(detail.prices, 'yearly');
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left w-full"
      style={{
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: 3,
        padding: 14,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        minHeight: 44,
      }}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div
            style={{
              fontFamily: sansFont,
              fontSize: 14,
              color: C.text,
              fontWeight: 500,
              lineHeight: 1.3,
              wordBreak: 'break-word',
            }}
          >
            {detail.addon.name}
          </div>
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 11,
              color: C.muted,
              marginTop: 2,
            }}
          >
            {detail.addon.slug} · {addonKindLabel(detail.addon.kind)}
          </div>
        </div>
        <Pill tone={planStatusTone(detail.addon.status)}>
          {detail.addon.status}
        </Pill>
      </div>
      <div
        className="flex items-center justify-between gap-3 flex-wrap"
        style={{
          fontFamily: monoFont,
          fontSize: 11,
          color: C.body,
        }}
      >
        <span>{formatPriceLabel(monthly)}</span>
        <span style={{ color: C.dim }}>·</span>
        <span>{formatPriceLabel(yearly)}</span>
        <span style={{ color: C.dim }}>·</span>
        <span>
          {detail.addon.unit_value.toLocaleString('en-US')}{' '}
          {addonUnitHint(detail.addon.kind)}
        </span>
      </div>
    </button>
  );
}
